import React, { useState, useEffect, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { apiFetch } from '../api/client';

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = n => (n == null ? '—' : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtDate = s => { if (!s) return '—'; const d = new Date(s); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }); };

const STATUS_COLOR = { active: '#2196f3', won: '#00e676', lost: '#ff1744', cancelled: '#888', cashed_out: '#ffab00' };
const TX_COLOR = { deposit: '#00e676', withdrawal: '#ff1744', bet_placed: '#ff9800', bet_won: '#00e676', bet_lost: '#ff1744', bet_cancelled: '#888', cashout: '#ffab00' };

const Card = ({ children, style }) => (
  <div style={{ background: 'var(--bs-surface, #111)', border: '1px solid var(--bs-border, #1e1e1e)', borderRadius: 12, padding: 16, ...style }}>
    {children}
  </div>
);

const Label = ({ children }) => (
  <span style={{ fontSize: 10, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #555)', textTransform: 'uppercase', letterSpacing: 1 }}>{children}</span>
);

const Value = ({ children, color }) => (
  <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--bs-ink, #e0e0e0)', fontFamily: 'JetBrains Mono', marginTop: 4 }}>{children}</div>
);

const Pill = ({ active, onClick, children }) => (
  <button onClick={onClick} style={{ background: active ? 'var(--bs-green, #00e676)' : 'var(--bs-border, #1a1a1a)', color: active ? 'var(--bs-bg, #000)' : 'var(--bs-body, #888)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'JetBrains Mono' }}>
    {children}
  </button>
);

const Skeleton = ({ w = '100%', h = 16, style }) => (
  <div style={{ width: w, height: h, background: 'var(--bs-border, #1a1a1a)', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite', ...style }} />
);

// ─── sub-components ──────────────────────────────────────────────────────────

function StatStrip({ profile, loading }) {
  if (loading) return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
      {[0,1,2].map(i => <Card key={i}><Skeleton h={50} /></Card>)}
    </div>
  );
  const pnlColor = profile?.pnl >= 0 ? '#00e676' : '#ff1744';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
      <Card>
        <Label>Balance</Label>
        <Value color="#00e676">{fmt(profile?.balance)}</Value>
      </Card>
      <Card>
        <Label>P&L</Label>
        <Value color={pnlColor}>{profile?.pnl >= 0 ? '+' : ''}{fmt(profile?.pnl)}</Value>
      </Card>
      <Card>
        <Label>Win Rate</Label>
        <Value>{profile?.win_rate ?? 0}%</Value>
      </Card>
    </div>
  );
}

const RANGES = ['7d', '30d', '90d', '1y', 'max'];

function BalanceChart({ userId, loading: profileLoading }) {
  const [range, setRange] = useState('7d');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async (r) => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/users/${userId}/balance-history?range=${r}`);
      const data = await res.json();
      setHistory(data.history || []);
    } catch (e) {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { if (userId) fetchHistory(range); }, [userId, range, fetchHistory]);

  const isGreen = history.length >= 2 && history[history.length - 1].balance >= history[0].balance;
  const color = isGreen ? '#00e676' : '#ff1744';

  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Label>Balance History</Label>
        <div style={{ display: 'flex', gap: 6 }}>
          {RANGES.map(r => (
            <button key={r} onClick={() => { setRange(r); fetchHistory(r); }}
              style={{ background: range === r ? color : 'transparent', color: range === r ? '#000' : 'var(--bs-muted, #555)', border: `1px solid ${range === r ? color : 'var(--bs-border, #333)'}`, borderRadius: 6, padding: '2px 8px', fontSize: 10, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontWeight: 600 }}>
              {r.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      {loading || profileLoading ? (
        <Skeleton h={120} />
      ) : history.length === 0 ? (
        <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--bs-muted, #444)', fontSize: 12 }}>
          No history yet — snapshots are recorded automatically
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={120}>
          <AreaChart data={history} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="balGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: 'var(--bs-muted, #555)', fontFamily: 'JetBrains Mono' }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 9, fill: 'var(--bs-muted, #555)', fontFamily: 'JetBrains Mono' }} tickLine={false} axisLine={false} width={50} tickFormatter={v => v.toFixed(0)} />
            <Tooltip
              contentStyle={{ background: 'var(--bs-bg, #0a0a0a)', border: '1px solid var(--bs-border, #222)', borderRadius: 8, fontSize: 11, fontFamily: 'JetBrains Mono' }}
              labelStyle={{ color: 'var(--bs-body, #888)' }}
              formatter={(v) => [fmt(v), 'Balance']}
            />
            <Area type="monotone" dataKey="balance" stroke={color} strokeWidth={2} fill="url(#balGrad)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

function BetCard({ bet }) {
  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid var(--bs-border, #1a1a1a)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <div style={{ fontSize: 12, color: 'var(--bs-body, #ccc)', flex: 1, marginRight: 8, lineHeight: 1.4 }}>{bet.question}</div>
        <span style={{ fontSize: 10, fontFamily: 'JetBrains Mono', color: STATUS_COLOR[bet.status] || '#888', background: 'var(--bs-border, #1a1a1a)', padding: '2px 7px', borderRadius: 10, whiteSpace: 'nowrap' }}>
          {bet.status === 'cancelled' ? 'EXPIRED' : bet.status.toUpperCase()}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 16, fontSize: 11, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #666)' }}>
        <span style={{ color: bet.side === 'yes' ? '#00e676' : '#ff1744' }}>{bet.side.toUpperCase()}</span>
        <span>{fmt(bet.amount)} staked</span>
        {bet.status === 'won' && <span style={{ color: '#00e676' }}>+{fmt(bet.payout)} won</span>}
        {bet.status === 'active' && <span>→ {fmt(bet.potential_payout)} potential</span>}
        {bet.status === 'cancelled' && <span style={{ color: '#888' }}>Market expired — +{fmt(bet.amount)} refunded</span>}
        <span style={{ marginLeft: 'auto' }}>{fmtDate(bet.created_at)}</span>
      </div>
    </div>
  );
}

function TxRow({ tx }) {
  const isPositive = tx.amount > 0;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--bs-border, #1a1a1a)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 10, fontFamily: 'JetBrains Mono', color: TX_COLOR[tx.type] || '#888', background: 'var(--bs-border, #1a1a1a)', padding: '2px 8px', borderRadius: 10 }}>
          {tx.type.replace('_', ' ').toUpperCase()}
        </span>
        <span style={{ fontSize: 11, color: 'var(--bs-muted, #666)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.description}</span>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 13, fontFamily: 'JetBrains Mono', fontWeight: 700, color: isPositive ? '#00e676' : '#ff1744' }}>
          {isPositive ? '+' : ''}{fmt(tx.amount)}
        </div>
        <div style={{ fontSize: 10, color: 'var(--bs-muted, #444)', fontFamily: 'JetBrains Mono' }}>{fmtDate(tx.created_at)}</div>
      </div>
    </div>
  );
}

function WinRateBar({ rate }) {
  return (
    <div style={{ height: 4, background: 'var(--bs-border, #1a1a1a)', borderRadius: 2, overflow: 'hidden', marginTop: 4 }}>
      <div style={{ height: '100%', width: `${Math.min(100, rate || 0)}%`, background: 'var(--bs-green, #00e676)', borderRadius: 2, transition: 'width 0.6s ease' }} />
    </div>
  );
}

function FavouriteCard({ fav, rank }) {
  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid var(--bs-border, #1a1a1a)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{ fontSize: 14, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #555)' }}>#{rank}</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--bs-ink, #e0e0e0)' }}>{fav.entity_name}</div>
          <div style={{ fontSize: 10, color: 'var(--bs-muted, #555)', fontFamily: 'JetBrains Mono' }}>{fav.entity_type} · {fav.bet_count} bet{fav.bet_count !== 1 ? 's' : ''}</div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 14, fontFamily: 'JetBrains Mono', fontWeight: 700, color: 'var(--bs-green, #00e676)' }}>{Math.round(fav.win_rate || 0)}%</div>
          <div style={{ fontSize: 10, color: 'var(--bs-muted, #555)', fontFamily: 'JetBrains Mono' }}>win rate</div>
        </div>
      </div>
      <WinRateBar rate={fav.win_rate} />
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

const SUB_TABS = ['Overview', 'Bets', 'Transactions', 'Favourites'];
const STATUS_FILTERS = ['all', 'active', 'won', 'lost', 'expired'];

export default function ProfilePage({ user, showAuth, showToast }) {
  const [subTab, setSubTab] = useState('Overview');
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Bets state
  const [bets, setBets] = useState([]);
  const [betsPage, setBetsPage] = useState(1);
  const [betsTotal, setBetsTotal] = useState(0);
  const [betsPages, setBetsPages] = useState(1);
  const [betsStatus, setBetsStatus] = useState('all');
  const [betsLoading, setBetsLoading] = useState(false);

  // Transactions state
  const [txs, setTxs] = useState([]);
  const [txPage, setTxPage] = useState(1);
  const [txTotal, setTxTotal] = useState(0);
  const [txPages, setTxPages] = useState(1);
  const [txLoading, setTxLoading] = useState(false);

  // ── Redirect if not logged in ──
  useEffect(() => {
    if (!user) showAuth();
  }, [user, showAuth]);

  // ── Fetch profile ──
  useEffect(() => {
    if (!user) return;
    setProfileLoading(true);
    apiFetch(`/api/users/${user.id}/profile`)
      .then(r => r.json())
      .then(data => { setProfile(data); setProfileLoading(false); })
      .catch(() => setProfileLoading(false));
  }, [user]);

  // ── Fetch bets ──
  const fetchBets = useCallback(async (page, status) => {
    if (!user) return;
    setBetsLoading(true);
    try {
      const res = await apiFetch(`/api/users/${user.id}/bets?page=${page}&status=${status}`);
      const data = await res.json();
      setBets(data.bets || []);
      setBetsPage(data.page || 1);
      setBetsTotal(data.total || 0);
      setBetsPages(data.pages || 1);
    } catch (e) {
      showToast('Failed to load bets', 'error');
    } finally {
      setBetsLoading(false);
    }
  }, [user, showToast]);

  // ── Fetch transactions ──
  const fetchTxs = useCallback(async (page) => {
    if (!user) return;
    setTxLoading(true);
    try {
      const res = await apiFetch(`/api/users/${user.id}/transactions?page=${page}`);
      const data = await res.json();
      setTxs(data.transactions || []);
      setTxPage(data.page || 1);
      setTxTotal(data.total || 0);
      setTxPages(data.pages || 1);
    } catch (e) {
      showToast('Failed to load transactions', 'error');
    } finally {
      setTxLoading(false);
    }
  }, [user, showToast]);

  // Fetch bets + txs on mount and when tab changes
  useEffect(() => {
    if (!user) return;
    fetchBets(1, 'all');
    fetchTxs(1);
  }, [user, fetchBets, fetchTxs]);

  if (!user) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: 'var(--bs-muted, #555)' }}>
        <div style={{ fontSize: 14, fontFamily: 'JetBrains Mono' }}>Sign in to view your profile</div>
        <button onClick={showAuth} style={{ marginTop: 16, background: 'var(--bs-green, #00e676)', color: 'var(--bs-bg, #000)', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>Sign In</button>
      </div>
    );
  }

  const handleStatusFilter = (s) => {
    setBetsStatus(s);
    fetchBets(1, s === 'expired' ? 'cancelled' : s);
  };

  // ─── Overview ────────────────────────────────────────────────────────────

  const renderOverview = () => (
    <>
      <StatStrip profile={profile} loading={profileLoading} />
      <BalanceChart userId={user?.id} loading={profileLoading} />

      {/* Recent Bets */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <Label>Recent Bets</Label>
          <button onClick={() => setSubTab('Bets')} style={{ fontSize: 10, fontFamily: 'JetBrains Mono', color: 'var(--bs-green, #00e676)', background: 'none', border: 'none', cursor: 'pointer' }}>SEE ALL →</button>
        </div>
        {betsLoading ? [0,1,2].map(i => <Skeleton key={i} h={44} style={{ marginBottom: 8 }} />) :
          bets.slice(0, 3).length === 0
            ? <div style={{ fontSize: 12, color: 'var(--bs-muted, #444)', padding: '8px 0' }}>No bets yet</div>
            : bets.slice(0, 3).map(b => <BetCard key={b.id} bet={b} />)
        }
      </Card>

      {/* Recent Transactions */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <Label>Recent Transactions</Label>
          <button onClick={() => setSubTab('Transactions')} style={{ fontSize: 10, fontFamily: 'JetBrains Mono', color: 'var(--bs-green, #00e676)', background: 'none', border: 'none', cursor: 'pointer' }}>SEE ALL →</button>
        </div>
        {txLoading ? [0,1,2].map(i => <Skeleton key={i} h={40} style={{ marginBottom: 8 }} />) :
          txs.slice(0, 3).length === 0
            ? <div style={{ fontSize: 12, color: 'var(--bs-muted, #444)', padding: '8px 0' }}>No transactions yet</div>
            : txs.slice(0, 3).map(t => <TxRow key={t.id} tx={t} />)
        }
      </Card>

      {/* Favourite Entities */}
      <Card>
        <Label>Favourite Entities</Label>
        {profileLoading ? [0,1,2].map(i => <Skeleton key={i} h={44} style={{ marginTop: 12 }} />) :
          (!profile?.favourites || profile.favourites.length === 0)
            ? <div style={{ fontSize: 12, color: 'var(--bs-muted, #444)', marginTop: 12 }}>Bet on markets to see your favourites</div>
            : profile.favourites.map((f, i) => <FavouriteCard key={f.entity_name} fav={f} rank={i + 1} />)
        }
      </Card>
    </>
  );

  // ─── Bets tab ────────────────────────────────────────────────────────────

  const renderBets = () => (
    <Card>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map(s => (
          <Pill key={s} active={betsStatus === s} onClick={() => handleStatusFilter(s)}>
            {s.toUpperCase()}
          </Pill>
        ))}
        <span style={{ fontSize: 11, color: 'var(--bs-muted, #555)', fontFamily: 'JetBrains Mono', alignSelf: 'center', marginLeft: 'auto' }}>{betsTotal} bet{betsTotal !== 1 ? 's' : ''}</span>
      </div>
      {betsLoading ? [0,1,2].map(i => <Skeleton key={i} h={60} style={{ marginBottom: 10 }} />) :
        bets.length === 0
          ? <div style={{ fontSize: 12, color: 'var(--bs-muted, #444)', padding: '20px 0', textAlign: 'center' }}>No bets found</div>
          : bets.map(b => <BetCard key={b.id} bet={b} />)
      }
      {betsPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 14 }}>
          <button disabled={betsPage <= 1} onClick={() => fetchBets(betsPage - 1, betsStatus)}
            style={{ background: 'var(--bs-border, #1a1a1a)', border: 'none', color: betsPage <= 1 ? '#333' : 'var(--bs-body, #888)', padding: '6px 14px', borderRadius: 6, cursor: betsPage <= 1 ? 'default' : 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>← Prev</button>
          <span style={{ fontSize: 11, color: 'var(--bs-muted, #555)', fontFamily: 'JetBrains Mono', alignSelf: 'center' }}>{betsPage} / {betsPages}</span>
          <button disabled={betsPage >= betsPages} onClick={() => fetchBets(betsPage + 1, betsStatus)}
            style={{ background: 'var(--bs-border, #1a1a1a)', border: 'none', color: betsPage >= betsPages ? '#333' : 'var(--bs-body, #888)', padding: '6px 14px', borderRadius: 6, cursor: betsPage >= betsPages ? 'default' : 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>Next →</button>
        </div>
      )}
    </Card>
  );

  // ─── Transactions tab ─────────────────────────────────────────────────────

  const renderTransactions = () => (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Label>Transactions</Label>
        <span style={{ fontSize: 11, color: 'var(--bs-muted, #555)', fontFamily: 'JetBrains Mono' }}>{txTotal} total</span>
      </div>
      {txLoading ? [0,1,2,3].map(i => <Skeleton key={i} h={44} style={{ marginBottom: 8 }} />) :
        txs.length === 0
          ? <div style={{ fontSize: 12, color: 'var(--bs-muted, #444)', padding: '20px 0', textAlign: 'center' }}>No transactions yet</div>
          : txs.map(t => <TxRow key={t.id} tx={t} />)
      }
      {txPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 14 }}>
          <button disabled={txPage <= 1} onClick={() => fetchTxs(txPage - 1)}
            style={{ background: 'var(--bs-border, #1a1a1a)', border: 'none', color: txPage <= 1 ? '#333' : 'var(--bs-body, #888)', padding: '6px 14px', borderRadius: 6, cursor: txPage <= 1 ? 'default' : 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>← Prev</button>
          <span style={{ fontSize: 11, color: 'var(--bs-muted, #555)', fontFamily: 'JetBrains Mono', alignSelf: 'center' }}>{txPage} / {txPages}</span>
          <button disabled={txPage >= txPages} onClick={() => fetchTxs(txPage + 1)}
            style={{ background: 'var(--bs-border, #1a1a1a)', border: 'none', color: txPage >= txPages ? '#333' : 'var(--bs-body, #888)', padding: '6px 14px', borderRadius: 6, cursor: txPage >= txPages ? 'default' : 'pointer', fontFamily: 'JetBrains Mono', fontSize: 11 }}>Next →</button>
        </div>
      )}
    </Card>
  );

  // ─── Favourites tab ───────────────────────────────────────────────────────

  const renderFavourites = () => (
    <Card>
      <Label>Favourite Entities</Label>
      {profileLoading ? [0,1,2].map(i => <Skeleton key={i} h={60} style={{ marginTop: 14 }} />) :
        (!profile?.favourites || profile.favourites.length === 0)
          ? <div style={{ fontSize: 12, color: 'var(--bs-muted, #444)', marginTop: 16, textAlign: 'center' }}>Place bets to build your favourites list</div>
          : profile.favourites.map((f, i) => <FavouriteCard key={f.entity_name} fav={f} rank={i + 1} />)
      }
    </Card>
  );

  // ─── RENDER ───────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '0 12px 24px' }}>
      {/* Header */}
      <div style={{ padding: '20px 0 16px' }}>
        <div style={{ fontSize: 11, fontFamily: 'JetBrains Mono', color: 'var(--bs-muted, #555)', marginBottom: 4 }}>PROFILE</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--bs-border, #1a1a1a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
            {(user.username || 'U')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--bs-ink, #e0e0e0)' }}>{user.display_name || user.username}</div>
            <div style={{ fontSize: 11, color: 'var(--bs-muted, #555)', fontFamily: 'JetBrains Mono' }}>@{user.username}</div>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--bs-border, #1a1a1a)', paddingBottom: 0 }}>
        {SUB_TABS.map(t => (
          <button key={t} onClick={() => setSubTab(t)}
            style={{ background: 'none', border: 'none', borderBottom: subTab === t ? '2px solid var(--bs-green, #00e676)' : '2px solid transparent', color: subTab === t ? 'var(--bs-green, #00e676)' : 'var(--bs-muted, #555)', padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans,sans-serif', transition: 'all 0.15s' }}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {subTab === 'Overview' && renderOverview()}
      {subTab === 'Bets' && renderBets()}
      {subTab === 'Transactions' && renderTransactions()}
      {subTab === 'Favourites' && renderFavourites()}
    </div>
  );
}
